// File utilities: browser downloads, CSV table export, vCard generation, and
// media (image/video/audio) metadata extraction — everything that produces or
// inspects a file the browser hands to (or takes from) the user.

import type { MediaDimensions } from "@auger/core"

// --- Browser download ---

// mimetype example: "text/csv;charset=utf-8"
export const downloadFile = function (filename: any, mimetype: any, data: any) {
  // Create an anchor element and inline data
  const anchor = document.createElement("a")
  anchor.href = "data:" + mimetype + "," + encodeURIComponent(data)
  anchor.target = "_blank"
  anchor.download = filename

  // Trigger the download
  anchor.click()
}

// --- CSV table export ---

// Read data from table with id <tableId> and download as CSV file
export const downloadCSVFromTable = function (tableId: any, separator = ",") {
  // Get the table into a CSV formatted string
  const csv = convertTableToCSV(tableId, separator)

  // Set the destination filename for the download
  const dateStr = new Date().toISOString().split("T")[0]
  const filename = `${dateStr}-${tableId}-table-export.csv`

  downloadFile(filename, "data:text/csv;charset=utf-8", csv)
}

// Convert a table referenced by it's HTML id into a CSV formatted string
export const convertTableToCSV = function (tableId: any, separator = ",") {
  // Select rows from table
  const rows = document.querySelectorAll("table#" + tableId + " tr")

  // Construct CSV
  const lines = []
  let rowspan: any = 0
  for (let i = 0; i < rows.length; i++) {
    const row = []
    const cols = rows[i]!.querySelectorAll("td, th")

    // Insert empty leading cells if a prior cell in the first column had rowspan set
    if (rowspan > 0) {
      row.push('""')
      rowspan--
    } else if (cols[0]) {
      rowspan = cols[0].getAttribute("rowspan") || 1
      rowspan--
    }

    for (let j = 0; j < cols.length; j++) {
      // Clean innertext to remove multiple spaces and jumpline (break csv)
      let data = (cols[j] as any).innerText
        .replace(/(\n\n)/, "; ")
        .replace(/(\r\n|\n|\r)/gm, "")
        .replace(/(\s\s)/gm, " ")
      // console.log(cols[j].innerText);

      // Escape double-quote with double-double-quote
      // see https://stackoverflow.com/questions/17808511/properly-escape-a-double-quote-in-csv
      data = data.replace(/"/g, '""')

      // Push escaped string
      row.push('"' + data + '"')
    }
    lines.push(row.join(separator))
  }

  // Join lines into single string
  return lines.join("\n")
}

// --- vCard generation ---

/**
 * Generate a vCard (VCF) file content from a person record
 * @param {Object} person - Person object with name, description, contacts
 * @returns {String} vCard formatted string
 */
export const generateVCard = (person: any) => {
  if (!person) {
    throw new Error("Person object is required")
  }

  const lines = []

  // vCard version
  lines.push("BEGIN:VCARD")
  lines.push("VERSION:3.0")

  // Full name (required)
  const name = person.name || "Unknown"
  lines.push(`FN:${escapeVCard(name)}`)

  // Structured name (last;first;middle;prefix;suffix)
  // For simplicity, we'll treat the first word as first name and rest as last name
  const nameParts = name.split(" ")
  const firstName = nameParts[0] || ""
  const lastName = nameParts.slice(1).join(" ") || ""
  lines.push(`N:${escapeVCard(lastName)};${escapeVCard(firstName)};;;`)

  // Title/Description
  if (person.description) {
    lines.push(`TITLE:${escapeVCard(person.description)}`)
  }

  // Process contacts if available
  const contacts = person.contacts || []

  // Email addresses
  const emails = contacts.filter((c: any) => c.contact_type === "email")
  emails.forEach((contact: any, index: any) => {
    const isPrimary = contact.is_primary || index === 0
    let type = isPrimary ? "PREF" : "INTERNET"

    // Try to infer email type from label
    if (contact.label) {
      const labelLower = contact.label.toLowerCase()
      if (labelLower.includes("work")) {
        type += ",WORK"
      } else if (labelLower.includes("home")) {
        type += ",HOME"
      }
    }

    lines.push(`EMAIL;TYPE=${type}:${escapeVCard(contact.value)}`)
  })

  // Phone numbers
  const phones = contacts.filter((c: any) => c.contact_type === "phone")
  phones.forEach((contact: any, index: any) => {
    const isPrimary = contact.is_primary || index === 0
    const typePrefix = isPrimary ? "PREF," : ""
    // Try to infer phone type from label
    const label = (contact.label || "").toLowerCase()
    let phoneType = "VOICE"
    if (label.includes("mobile") || label.includes("cell")) {
      phoneType = "CELL"
    } else if (label.includes("work")) {
      phoneType = "WORK"
    } else if (label.includes("home")) {
      phoneType = "HOME"
    }
    lines.push(`TEL;TYPE=${typePrefix}${phoneType}:${escapeVCard(contact.value)}`)
  })

  // URLs/Links
  const links = contacts.filter((c: any) => c.contact_type === "url")
  const linkNotes: any[] = []
  links.forEach((contact: any) => {
    // Add the URL property (no label per vCard 3.0 spec)
    lines.push(`URL:${escapeVCard(contact.value)}`)
    // If label is present, add it to notes for context
    if (contact.label) {
      linkNotes.push(`${escapeVCard(contact.label)}: ${escapeVCard(contact.value)}`)
    }
  })

  // Addresses
  const addresses = contacts.filter((c: any) => c.contact_type === "location")
  const addressNotes: any[] = []
  addresses.forEach((contact: any, index: any) => {
    const isPrimary = contact.is_primary || index === 0
    const type = isPrimary ? "PREF" : "INTL"
    // vCard address format: ;;street;city;state;postal;country
    // We'll put the full address in the street field for simplicity
    lines.push(`ADR;TYPE=${type}:;;${escapeVCard(contact.value)};;;;`)
    // If label is present, add it to notes for context
    if (contact.label) {
      addressNotes.push(`${escapeVCard(contact.label)}: ${escapeVCard(contact.value)}`)
    }
  })

  // Tags as categories
  if (person.tags && person.tags.length > 0) {
    const categories = person.tags.map((tag: any) => escapeVCard(tag.name)).join(",")
    lines.push(`CATEGORIES:${categories}`)
  }

  // Note with organizations, link labels, and address labels if available
  const noteLines = []

  const organizations = person.organizations || []
  if (organizations.length > 0) {
    const orgNames = organizations.map((org: any) => escapeVCard(org.name)).join(", ")
    noteLines.push(`Associated with: ${orgNames}`)
  }

  if (linkNotes.length > 0) {
    noteLines.push("Links: " + linkNotes.join(", "))
  }

  if (addressNotes.length > 0) {
    noteLines.push("Addresses: " + addressNotes.join(", "))
  }

  if (noteLines.length > 0) {
    lines.push(`NOTE:${noteLines.join(" | ")}`)
  }

  // Timestamp
  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
  lines.push(`REV:${timestamp}`)

  lines.push("END:VCARD")

  return lines.join("\r\n")
}

/**
 * Escape special characters in vCard values
 * @param {String} value - Value to escape
 * @returns {String} Escaped value
 */
const escapeVCard = (value: any) => {
  if (!value) return ""

  return String(value)
    .replace(/\\/g, "\\\\") // Backslash
    .replace(/;/g, "\\;") // Semicolon
    .replace(/,/g, "\\,") // Comma
    .replace(/\n/g, "\\n") // Newline
}

/**
 * Generate a filename for the vCard
 * @param {Object} person - Person object
 * @returns {String} Filename for the vCard
 */
export const getVCardFilename = (person: any) => {
  const name = person.name || "contact"
  // Sanitize filename
  const sanitized = name
    .replace(/[^a-z0-9]/gi, "_")
    .replace(/_+/g, "_")
    .toLowerCase()
  return `${sanitized}.vcf`
}

/**
 * Assemble the shape `generateVCard` reads from a loaded person record.
 * Organizations come from the record's listings — the same org can appear on
 * several lists, so they're de-duplicated by id.
 * @param {Object} record - Loaded person record (name, description, listings)
 * @param {Object} extras - Resolved `contacts` and `tags` rows for the record
 * @returns {Object} Person object accepted by `generateVCard`
 */
export const buildVCardPerson = (
  record: any,
  { contacts = [], tags = [] }: { contacts?: any[]; tags?: any[] } = {}
) => {
  const byId = new Map<any, any>()
  for (const listing of record?.listings || []) {
    const parent = listing.list?.parent
    if (parent?.type === "organization") byId.set(parent.id, parent)
  }

  return {
    ...record,
    contacts,
    organizations: [...byId.values()],
    tags,
  }
}

/**
 * Build and download a vCard for a loaded person record.
 * @param {Object} record - Loaded person record
 * @param {Object} extras - Resolved `contacts` and `tags` rows for the record
 */
export const downloadVCard = (record: any, extras: { contacts?: any[]; tags?: any[] } = {}) => {
  const person = buildVCardPerson(record, extras)
  downloadFile(getVCardFilename(person), "text/vcard;charset=utf-8", generateVCard(person))
}

// --- Media metadata extraction ---

/**
 * Extract dimensions from an image file
 */
export function extractImageDimensions(file: Blob) {
  return new Promise<MediaDimensions>((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("Failed to load image"))
    }

    img.src = url
  })
}

/**
 * Extract dimensions and duration from a video file
 */
export function extractVideoDimensions(file: Blob) {
  return new Promise<MediaDimensions>((resolve, reject) => {
    const video = document.createElement("video")
    const url = URL.createObjectURL(file)

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: Math.round(video.duration * 100) / 100,
      })
    }

    video.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("Failed to load video"))
    }

    video.src = url
  })
}

/**
 * Extract duration from an audio file
 */
export function extractAudioDuration(file: Blob) {
  return new Promise<MediaDimensions>((resolve, reject) => {
    const audio = document.createElement("audio")
    const url = URL.createObjectURL(file)

    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve({
        duration: Math.round(audio.duration * 100) / 100,
      })
    }

    audio.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("Failed to load audio"))
    }

    audio.src = url
  })
}

/**
 * Extract dimensions/duration from a media file based on its MIME type
 */
export function extractMediaDimensions(file: Blob): Promise<MediaDimensions | null> {
  const mimeType = file.type

  if (mimeType.startsWith("image/")) {
    return extractImageDimensions(file)
  }

  if (mimeType.startsWith("video/")) {
    return extractVideoDimensions(file)
  }

  if (mimeType.startsWith("audio/")) {
    return extractAudioDuration(file)
  }

  return Promise.resolve(null)
}
